import is from '@sindresorhus/is';
import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { IconChevronRightSmall } from '~/ui/core/icons';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import { CommentableDescriptionSection } from '../../../comments';
import { PlainDescriptionSection } from '../../../comments/CommentableDescriptionSection';
import type { TaskManagementTaskRef } from '../../../data-contract/task-management';
import { isAutomationAssessmentPending, type ProfileTaskAutomation } from '../api/profile-task-automation-adapter';
import { useAutomationRecommendationsEnabled } from '../api/use-work-task-automation-recommendation';
import { AutomationAiNotice, AutomationAssessmentSection } from '../automation-assessment/AutomationAssessmentSection';
import { AutomationRecommendationSection } from '../automation-assessment/AutomationRecommendationSection';
import { toPercent } from '../automation-assessment/automation-assessment-scoring';
import {
    automationAssessmentTranslationKeys,
    automationRecommendationTranslationKeys,
} from '../automation-assessment/automation-assessment-translations';
import { isAutomationStepTilePayload } from '../automation-assessment/automation-step-tile-url';
import { TaskStatsRow } from '../automation-assessment/TaskStatsRow';
import type { ProfileTaskEffort, ProfileTaskSkill, ProfileTaskWorkflowStep } from '../model/profile-task';
import { toProfileTaskRichTextHtml } from '../profile-task-rich-text';
import { profileTaskTranslationKeys } from '../profile-task-translations';
import { TaskWorkflowStepPartCommentable } from './profile-task-comments-document';
import type { TaskMetaRow } from './TaskFieldPrimitives';
import {
    TASK_AUDIT_BOX_ROW_CLASS,
    TASK_AUDIT_BOX_ROWS_CLASS,
    TASK_LIST_ROW_DIVIDER_CLASS,
    TaskAuditBox,
    TaskDetailsBox,
    TaskFieldSection,
    TaskWorkflowStepRow,
} from './TaskFieldPrimitives';
import { TaskSkillRow } from './TaskSkillRow';
import { useOpenTileOfTask } from './use-open-tile-of-task';

export type TaskDetailCommentable = {
    readonly itemId: string;
    readonly newThreadCreationDisabled: boolean;
};

export type TaskDetailRelatedTask = TaskManagementTaskRef;

/** The automation tiles a task can open beside it; a surface that cannot open them leaves the scores static. */
export type TaskDetailAutomationActions = {
    readonly openAssessment?: () => void;
    readonly openBusinessImpact?: () => void;
    readonly openRecommendation: (automationId: string) => void;
    readonly openStep?: (workflowStepId: string) => void;
};

export type TaskDetailRelations = {
    readonly children: readonly TaskDetailRelatedTask[];
    /** Leaves out the parents and children, e.g. where tasks are not seen as a hierarchy. */
    readonly hidesHierarchy?: boolean;
    /** Opens a related task; without it the related tasks are listed only. */
    readonly onOpenTask: ((task: TaskDetailRelatedTask) => void) | undefined;
    readonly parents: readonly TaskDetailRelatedTask[];
    readonly related: readonly TaskDetailRelatedTask[];
};

/** The task as every task detail tile shows it, whether it comes from a job profile, task management or an alignment. */
export type TaskDetail = {
    readonly automation: ProfileTaskAutomation | undefined;
    readonly description: string | undefined;
    readonly effort: ProfileTaskEffort | undefined;
    readonly skills: readonly ProfileTaskSkill[];
    readonly taskId: string;
    readonly workflowSteps: readonly ProfileTaskWorkflowStep[];
};

const RelationBox: FunctionComponent<{
    readonly onOpen: ((task: TaskDetailRelatedTask) => void) | undefined;
    readonly tasks: readonly TaskDetailRelatedTask[];
    readonly testId: string;
    readonly title: string;
}> = ({ onOpen, tasks, testId, title }) => {
    if (tasks.length === 0) {
        return null;
    }

    return (
        <TaskAuditBox testId={testId} title={title}>
            <ul className={`${TASK_AUDIT_BOX_ROWS_CLASS} list-none`}>
                {tasks.map((task) => (
                    <li key={task.id} className="list-none">
                        {onOpen === undefined ? (
                            <div
                                className={`${TASK_AUDIT_BOX_ROW_CLASS} text-xs font-normal leading-normal text-text-primary`}
                            >
                                <span className="min-w-0 flex-1 truncate">{task.title}</span>
                            </div>
                        ) : (
                            <Button
                                className={`${TASK_AUDIT_BOX_ROW_CLASS} m-0 w-full border-none bg-transparent text-left text-xs font-normal leading-normal text-text-primary hover:bg-surface-hover`}
                                variant="plain"
                                onClick={() => onOpen(task)}
                            >
                                <span className="min-w-0 flex-1 truncate">{task.title}</span>
                                <IconChevronRightSmall className="shrink-0 text-text-content-faint" size={16} />
                            </Button>
                        )}
                    </li>
                ))}
            </ul>
        </TaskAuditBox>
    );
};

const DescriptionSection: FunctionComponent<{
    readonly commentable: TaskDetailCommentable | undefined;
    readonly description: string | undefined;
}> = ({ commentable, description }) => {
    if (!is.nonEmptyString(description)) {
        return null;
    }

    // The description is the tile's lead paragraph, so it renders without a heading.
    if (commentable === undefined) {
        return (
            <section>
                <PlainDescriptionSection html={toProfileTaskRichTextHtml(description)} />
            </section>
        );
    }

    return (
        <CommentableDescriptionSection
            html={toProfileTaskRichTextHtml(description)}
            itemId={commentable.itemId}
            newThreadCreationDisabled={commentable.newThreadCreationDisabled}
            sectionKey="description"
            targetKind="description"
        />
    );
};

const WorkflowStepMeta: FunctionComponent<{
    readonly fraction: number | undefined;
    readonly isPending: boolean;
}> = ({ fraction, isPending }) => {
    const translate = useTranslate();

    if (isPending) {
        return <SkeletonBase className="mt-s h-3 w-24" />;
    }

    if (fraction === undefined) {
        return null;
    }

    return (
        <span
            className="mt-s text-body-xxs tabular-nums text-text-muted"
            data-testid="automation-assessment-step-score"
        >
            {translate(automationRecommendationTranslationKeys.automatableValue, { value: toPercent(fraction) })}
        </span>
    );
};

/**
 * The task's workflow as one list in the same box as the recommended agents: each step with its
 * number, title, description and automatable share, opening its step tile.
 */
const WorkflowSection: FunctionComponent<{
    readonly automation: ProfileTaskAutomation | undefined;
    readonly commentable: TaskDetailCommentable | undefined;
    readonly onOpenStep: ((workflowStepId: string) => void) | undefined;
    readonly steps: readonly ProfileTaskWorkflowStep[];
    readonly taskId: string;
    readonly testId: string;
}> = ({ automation, commentable, onOpenStep, steps, taskId, testId }) => {
    const translate = useTranslate();
    const isAssessing = isAutomationAssessmentPending(automation);
    const scoreByStepId = new Map(automation?.assessment?.steps.map((step) => [step.workflowStepId, step.score]));
    const openStepId = useOpenTileOfTask(isAutomationStepTilePayload, taskId)?.workflowStepId;

    if (steps.length === 0) {
        return null;
    }

    return (
        <TaskAuditBox title={translate(profileTaskTranslationKeys.workflow)}>
            <ol className={`${TASK_AUDIT_BOX_ROWS_CLASS} list-none`} data-testid={testId}>
                {steps.map((step, index) => {
                    const canOpen = onOpenStep !== undefined && scoreByStepId.has(step.workflowStepId);
                    const isSelected = step.workflowStepId === openStepId;

                    return (
                        <li key={step.workflowStepId} className={`list-none ${TASK_LIST_ROW_DIVIDER_CLASS}`}>
                            <TaskWorkflowStepRow
                                description={step.description}
                                index={index}
                                isSelected={isSelected}
                                meta={
                                    <WorkflowStepMeta
                                        fraction={scoreByStepId.get(step.workflowStepId)}
                                        isPending={isAssessing}
                                    />
                                }
                                openLabel={`${translate(automationAssessmentTranslationKeys.openStep)}: ${step.title}`}
                                title={step.title}
                                variant="list"
                                onOpen={canOpen ? () => onOpenStep(step.workflowStepId) : undefined}
                                {...(commentable === undefined
                                    ? {}
                                    : {
                                          wrapDescription: (content: ReactNode) => (
                                              <TaskWorkflowStepPartCommentable
                                                  itemId={commentable.itemId}
                                                  kind="workflow-step-description"
                                                  newThreadCreationDisabled={commentable.newThreadCreationDisabled}
                                                  sourceText={step.description ?? ''}
                                                  stepIndex={index}
                                              >
                                                  {content}
                                              </TaskWorkflowStepPartCommentable>
                                          ),
                                          wrapTitle: (content: ReactNode) => (
                                              <TaskWorkflowStepPartCommentable
                                                  itemId={commentable.itemId}
                                                  kind="workflow-step-title"
                                                  newThreadCreationDisabled={commentable.newThreadCreationDisabled}
                                                  sourceText={step.title}
                                                  stepIndex={index}
                                              >
                                                  {content}
                                              </TaskWorkflowStepPartCommentable>
                                          ),
                                      })}
                            />
                        </li>
                    );
                })}
            </ol>
        </TaskAuditBox>
    );
};

// Skills carry no target in the work-task comments api the task tiles post to, so they are read
// only here until that api gains one.
const SkillsSection: FunctionComponent<{
    readonly onOpenSkill: (skill: ProfileTaskSkill) => void;
    readonly skills: readonly ProfileTaskSkill[];
    readonly testId: string;
}> = ({ onOpenSkill, skills, testId }) => {
    const translate = useTranslate();

    if (skills.length === 0) {
        return null;
    }

    return (
        <TaskFieldSection title={translate(profileTaskTranslationKeys.skills)}>
            <ul className="m-0 flex list-none flex-col p-0" data-testid={testId}>
                {skills.map((skill) => (
                    <li key={skill.skillId} className="list-none">
                        <TaskSkillRow skill={skill} onClick={() => onOpenSkill(skill)} />
                    </li>
                ))}
            </ul>
        </TaskFieldSection>
    );
};

/**
 * The body of every task detail tile: description, automation potential, workflow, skills, recommended
 * automations, related tasks and details. Each surface maps its task into `TaskDetail` and passes the
 * actions it can offer; what it leaves out is shown static or not at all.
 */
export const TaskDetailFields: FunctionComponent<{
    readonly automationActions?: TaskDetailAutomationActions;
    readonly commentable?: TaskDetailCommentable;
    readonly metaRows: readonly TaskMetaRow[];
    readonly onOpenSkill: (skill: ProfileTaskSkill) => void;
    /** Work tasks relate to other tasks; job profile tasks do not. */
    readonly relations?: TaskDetailRelations;
    readonly task: TaskDetail;
    /** Keeps the surface's established test ids, `<prefix>-fields`, `<prefix>-workflow` and so on. */
    readonly testIdPrefix: 'profile-task' | 'work-task';
}> = ({ automationActions, commentable, metaRows, onOpenSkill, relations, task, testIdPrefix }) => {
    const translate = useTranslate();
    const hasAutomationRecipes = useAutomationRecommendationsEnabled();
    const { automation } = task;
    const assessment = automation?.assessment;
    const isAssessing = isAutomationAssessmentPending(automation);
    const showAiNotice =
        hasAutomationRecipes && (assessment !== undefined || automation?.recommendationState === 'succeeded');

    return (
        <div className="flex flex-col gap-6xl" data-testid={`${testIdPrefix}-fields`}>
            <DescriptionSection commentable={commentable} description={task.description} />
            {hasAutomationRecipes ? (
                <TaskStatsRow
                    businessImpact={assessment?.businessImpact}
                    effort={task.effort}
                    isAssessing={isAssessing}
                    stepCount={task.workflowSteps.length}
                    taskScore={assessment?.taskScore}
                    onOpenAssessment={automationActions?.openAssessment}
                    onOpenBusinessImpact={automationActions?.openBusinessImpact}
                />
            ) : assessment !== undefined || isAssessing ? (
                <AutomationAssessmentSection
                    assessment={assessment}
                    isPending={isAssessing}
                    onOpenDetails={automationActions?.openAssessment}
                />
            ) : null}
            <WorkflowSection
                automation={automation}
                commentable={commentable}
                steps={task.workflowSteps}
                taskId={task.taskId}
                testId={`${testIdPrefix}-workflow`}
                onOpenStep={automationActions?.openStep}
            />
            <SkillsSection onOpenSkill={onOpenSkill} skills={task.skills} testId={`${testIdPrefix}-skills`} />
            <AutomationRecommendationSection
                state={automation?.recommendationState}
                taskId={task.taskId}
                onOpenAutomation={automationActions?.openRecommendation}
            />
            {relations === undefined ? null : (
                <>
                    {relations.hidesHierarchy === true ? null : (
                        <>
                            <RelationBox
                                testId={`${testIdPrefix}-parents`}
                                title={translate('pages.job-architecture-v2.tasks.relation.parents')}
                                tasks={relations.parents}
                                onOpen={relations.onOpenTask}
                            />
                            <RelationBox
                                testId={`${testIdPrefix}-children`}
                                title={translate('pages.job-architecture-v2.tasks.relation.children')}
                                tasks={relations.children}
                                onOpen={relations.onOpenTask}
                            />
                        </>
                    )}
                    <RelationBox
                        testId={`${testIdPrefix}-related`}
                        title={translate('pages.job-architecture-v2.tasks.relation.related')}
                        tasks={relations.related}
                        onOpen={relations.onOpenTask}
                    />
                </>
            )}
            {metaRows.length > 0 || showAiNotice ? (
                // The notice qualifies the estimates above it, so it reads as their footnote.
                <div className="flex flex-col gap-s">
                    <TaskDetailsBox rows={metaRows} />
                    {showAiNotice ? <AutomationAiNotice /> : null}
                </div>
            ) : null}
        </div>
    );
};
